import { Action, createReducer } from '@reduxjs/toolkit'
import { get, isEmpty } from 'lodash-es'
import { REHYDRATE } from 'redux-persist'

import {
  Capacity,
  ProviderExpeditedBookingConfig,
  ProviderUser,
} from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import * as mixpanelTracker from '../../../mixpanel/mixpanelTracking'
import { ROLES, SUPERVISOR_ROLES } from '../common/constants/appConstants'
import {
  GET_PROVIDER_CAPACITY,
  LOGIN,
  LOGOUT,
  REFRESH_API_TOKEN,
  REGISTER_SUCCESSFUL,
  REPLACE_USER,
  SAVE_LAST_AUTHORIZED_ROUTE,
  SET_PRACTICE_PROVIDERS,
  SET_PROVIDER_CAPACITY,
  SET_SELECTED_TIMEZONE,
  SUBMIT_PROVIDER_OUTCOMES_CONSENT,
  UPDATE_CONFIG,
  UPDATE_EXPEDITED_BOOKING_STATUS,
  UPDATE_GRACE_PERIOD,
  UPDATE_IN_PERSON_PREFERENCE_STATUS,
} from '../common/constants/reduxConstants'
import { RoleConfig, roleConfigs } from '../common/roleConfigs'
import { hasRole } from '../common/utils/utils'

type AuthState = {
  isLoggedIn: boolean
  config?: RoleConfig
  user?: ProviderUser
  apiToken: string
  hydrated: boolean
  supervisor?: ProviderUser
  lyratherapy_providers: ProviderUser[]
  practiceAdmin?: ProviderUser
  practiceAdminConfig?: RoleConfig
  supervisorConfig?: RoleConfig
  practice_providers?: ProviderUser[]
  impersonatedProviderId?: string
  lastAuthorizedRoute?: string
  supervisorSelectedProviderId?: string
  selectedTimeZone?: string
}

// Extended user type with roles array
type UserWithRoles = ProviderUser & { roles: string[]; lyratherapy_providers?: ProviderUser[] }

// Action payload interfaces for type-safe reducer cases
interface LoginAction extends Action<typeof LOGIN> {
  payload: {
    user: UserWithRoles
    access_token: string
    configOverrides?: Partial<RoleConfig>
  }
}

interface ReplaceUserAction extends Action<typeof REPLACE_USER> {
  user: UserWithRoles
}

interface UpdateConfigAction extends Action<typeof UPDATE_CONFIG> {
  payload: Partial<RoleConfig>
}

interface RehydrateAction extends Action<typeof REHYDRATE> {
  payload?: {
    auth?: AuthState
  }
}

interface StringPayloadAction extends Action {
  payload: string
}

interface CapacityFulfilledAction extends Action {
  payload: {
    data: {
      capacity_value: Capacity
      available: boolean
      capacity_updated_at: string
    }
  }
}

interface CapacityAction extends Action {
  data: Capacity
}

interface GracePeriodAction extends Action {
  data: { value: number }
}

interface ExpeditedBookingAction extends Action {
  data: { value: ProviderExpeditedBookingConfig }
}

interface InPersonPreferenceAction extends Action {
  data: { in_person_preference: boolean }
}

interface SetTimezoneAction extends Action<typeof SET_SELECTED_TIMEZONE> {
  payload: string
}

interface PracticeProvidersAction extends Action<typeof SET_PRACTICE_PROVIDERS> {
  payload: ProviderUser[]
}

const initialState: AuthState = {
  isLoggedIn: false,
  apiToken: '',
  hydrated: false,
  lyratherapy_providers: [],
  practice_providers: [],
  lastAuthorizedRoute: undefined,
}

export default createReducer<AuthState>(initialState, (builder) => {
  builder.addCase(LOGIN, (state, action: LoginAction) => {
    state.user = action.payload.user
    state.isLoggedIn = true
    state.apiToken = action.payload.access_token

    action.payload.user.roles
      .filter((role) => role in roleConfigs)
      .sort((a, b) => Object.keys(roleConfigs).indexOf(a) - Object.keys(roleConfigs).indexOf(b))
      .forEach((role) => {
        state.config = { ...state.config, ...roleConfigs[role] }
      })

    if (action.payload.configOverrides) {
      state.config = { ...state.config, ...action.payload.configOverrides }
    }
    if (hasRole(action.payload.user.roles, SUPERVISOR_ROLES)) {
      state.supervisor = action.payload.user
      state.supervisorConfig = state.config
      state.lyratherapy_providers = action.payload.user.lyratherapy_providers ?? []
      const isSelfInProviderList = state.lyratherapy_providers.some((p) => p.id === action.payload.user.id)
      if (isSelfInProviderList) {
        state.supervisorSelectedProviderId = action.payload.user.id
      }
    }
    if (hasRole(action.payload.user.roles, [ROLES.PRACTICES_ADMIN])) {
      state.practiceAdmin = action.payload.user
      state.practiceAdminConfig = state.config
    }
    return state
  })
  builder.addCase(UPDATE_CONFIG, (state, action: UpdateConfigAction) => {
    state.config = { ...state.config, ...action.payload }
    return state
  })
  builder.addCase(LOGOUT, (state) => {
    // Logging out shouldn't mark `hydrated` to `false`. Since the initialState value is `false`, we set it to `true` again
    state.hydrated = true
    return state
  })
  builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
    const incoming = action.payload?.auth
    state = { ...state, ...incoming }
    mixpanelTracker.initializeTrackingWithProxy()
    if (incoming?.user && !isEmpty(get(incoming, 'user'))) {
      mixpanelTracker.setUser(incoming.user)
    }
    state.hydrated = true
    return state
  })
  builder.addCase(SAVE_LAST_AUTHORIZED_ROUTE, (state, action: StringPayloadAction) => {
    state.lastAuthorizedRoute = action.payload
    return state
  })
  builder.addCase(REGISTER_SUCCESSFUL, (state) => {
    if (state.user && 'id' in state.user) {
      state.user.is_registered = true
    }
    return state
  })
  builder.addCase(SUBMIT_PROVIDER_OUTCOMES_CONSENT, (state) => {
    if (state.user && 'id' in state.user) {
      state.user.outcomes_agreed = true
    }
    return state
  })
  builder.addCase(REFRESH_API_TOKEN, (state, action: StringPayloadAction) => {
    state.apiToken = action.payload
    return state
  })
  builder.addCase(REPLACE_USER, (state, action: ReplaceUserAction) => {
    const userConfig = action.user.roles
      .filter((role) => role in roleConfigs)
      .sort((a, b) => Object.keys(roleConfigs).indexOf(a) - Object.keys(roleConfigs).indexOf(b))
      .reduce((compositeConfig, role) => ({ ...compositeConfig, ...roleConfigs[role] }), {} as RoleConfig)

    // Track if this is a practice admin impersonating a provider
    if (state.practiceAdmin && action.user.id !== state.practiceAdmin.id) {
      state.impersonatedProviderId = action.user.id
      // Use the provider's config to show their actual navigation and feature experience
      // The practice admin can return to their view via the PracticeAdminImpersonationBar toggle
      state.config = userConfig
    } else {
      state.impersonatedProviderId = undefined
      state.config = userConfig
    }

    if (state.supervisor) {
      state.supervisorSelectedProviderId = action.user.id
      state.selectedTimeZone = undefined
    } else {
      state.supervisorSelectedProviderId = undefined
      state.selectedTimeZone = undefined
    }

    state.user = action.user
    return state
  })
  builder.addCase(`${SET_PROVIDER_CAPACITY}_FULFILLED`, (state, action: CapacityFulfilledAction) => {
    if (state.user && 'id' in state.user) {
      state.user.capacity_value = action.payload.data.capacity_value
      state.user.available = action.payload.data.available
      state.user.capacity_updated_at = action.payload.data.capacity_updated_at
    }
    return state
  })
  builder.addCase(GET_PROVIDER_CAPACITY, (state, action: CapacityAction) => {
    if (state.user && 'id' in state.user) {
      state.user.capacity_value = action.data
    }
    return state
  })
  builder.addCase(UPDATE_GRACE_PERIOD, (state, action: GracePeriodAction) => {
    if (state.user && 'id' in state.user) {
      state.user.grace_period = action.data.value
    }
    return state
  })
  builder.addCase(UPDATE_EXPEDITED_BOOKING_STATUS, (state, action: ExpeditedBookingAction) => {
    if (state.user && 'id' in state.user) {
      state.user.expedited_booking_config = action.data.value
    }
    return state
  })
  builder.addCase(UPDATE_IN_PERSON_PREFERENCE_STATUS, (state, action: InPersonPreferenceAction) => {
    if (state.user && 'id' in state.user) {
      state.user.in_person_preference = action.data.in_person_preference
    }
    return state
  })
  builder.addCase(SET_SELECTED_TIMEZONE, (state, action: SetTimezoneAction) => {
    state.selectedTimeZone = action.payload
    return state
  })
  builder.addCase(SET_PRACTICE_PROVIDERS, (state, action: PracticeProvidersAction) => {
    state.practice_providers = action.payload
    return state
  })
})
